import React, { useState, useRef, useEffect, Suspense } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls, Stars, Float } from "@react-three/drei";
import * as THREE from "three";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";
import { experience } from "../../data/experience";
import { certifications } from "../../data/skills";
import CompanyCard from "./CompanyCard";
import WorkTimeline from "./WorkTimeline";

// 3D Floating Geometry Component
const FloatingGeometry = ({ position, color, scale = 1 }) => {
  const meshRef = useRef();

  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.rotation.x =
        Math.sin(state.clock.elapsedTime * 0.5) * 0.3;
      meshRef.current.rotation.y = state.clock.elapsedTime * 0.2;
      meshRef.current.position.y =
        position[1] + Math.sin(state.clock.elapsedTime * 0.3) * 0.5;
    }
  });

  return (
    <Float speed={1.4} rotationIntensity={1} floatIntensity={2}>
      <mesh ref={meshRef} position={position} scale={scale}>
        <icosahedronGeometry args={[1, 0]} />
        <meshStandardMaterial
          color={color}
          wireframe={true}
          transparent
          opacity={0.6}
        />
      </mesh>
    </Float>
  );
};

// Particle System Component
const ParticleField = () => {
  const points = useRef();
  const particleCount = 100;

  const positions = new Float32Array(particleCount * 3);
  for (let i = 0; i < particleCount; i++) {
    positions[i * 3] = (Math.random() - 0.5) * 20;
    positions[i * 3 + 1] = (Math.random() - 0.5) * 20;
    positions[i * 3 + 2] = (Math.random() - 0.5) * 20;
  }

  useFrame((state) => {
    if (points.current) {
      points.current.rotation.y = state.clock.elapsedTime * 0.05;
    }
  });

  return (
    <points ref={points}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={particleCount}
          array={positions}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial size={0.05} color="#10b981" transparent opacity={0.6} />
    </points>
  );
};

const ExperienceSection = () => {
  const [activeView, setActiveView] = useState("timeline"); // "timeline" or "companies"
  const [activeExperience, setActiveExperience] = useState(0);
  const [activeCompany, setActiveCompany] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const { elementRef, isIntersecting } = useIntersectionObserver({
    threshold: 0.01,
    rootMargin: "0px 0px -50px 0px",
    triggerOnce: true,
  });

  // Transform your existing experience data to match the new component structure
  const transformedExperience = experience.map((job, index) => ({
    id: job.id,
    company: job.company,
    position: job.title,
    duration: job.duration,
    location: job.location || "Remote", // Add location if not in your data
    type: job.type || "Full-time", // Add type if not in your data
    description: job.description,
    responsibilities: job.responsibilities,
    technologies: job.technologies || [], // Add technologies if not in your data
    achievements: job.achievements || [], // Add achievements if not in your data
    logo: job.logo || null,
    achievement: job.featured || false, // Add featured/achievement flag if not in your data
  }));

  // Company data for card view
  const companyData = transformedExperience.map((exp, index) => ({
    ...exp,
    name: exp.company,
    id: index + 1,
  }));

  const handleViewToggle = async (view) => {
    if (view !== activeView) {
      setIsLoading(true);
      await new Promise((resolve) => setTimeout(resolve, 300));
      setActiveView(view);
      setIsLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.7,
        ease: [0.25, 0.46, 0.45, 0.94],
      },
    },
  };

  const statsData = [
    { label: "Years Experience", value: `4+` },
    { label: "Projects Completed", value: "32+" },
    { label: "Technologies Mastered", value: "15+" },
    { label: "Client Satisfaction", value: "99.99%" },
  ];

  return (
    <section
      id="experience"
      className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-900 text-white relative overflow-hidden"
      ref={elementRef}
    >
      {/* 3D Background Canvas */}
      <div className="absolute inset-0 opacity-20">
        <Canvas camera={{ position: [0, 0, 10], fov: 60 }}>
          <Suspense fallback={null}>
            <ambientLight intensity={0.5} />
            <pointLight position={[10, 10, 10]} />
            <FloatingGeometry
              position={[-5, 2, 0]}
              color="#10b981"
              scale={0.8}
            />
            <FloatingGeometry
              position={[5, -2, -2]}
              color="#14b8a6"
              scale={0.6}
            />
            <FloatingGeometry
              position={[0, 3, -3]}
              color="#059669"
              scale={0.4}
            />
            <ParticleField />
            <Stars
              radius={50}
              depth={50}
              count={1000}
              factor={4}
              saturation={0}
              fade
            />
          </Suspense>
        </Canvas>
      </div>

      {/* Enhanced Background Elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-500 rounded-full filter blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-72 h-72 bg-primary-700 rounded-full filter blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-3/4 left-1/2 w-64 h-64 bg-accent-400 rounded-full filter blur-2xl animate-pulse delay-2000" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isIntersecting ? "visible" : "hidden"}
        >
          {/* Enhanced Section Header */}
          <motion.div variants={itemVariants} className="text-center mb-16">
            <motion.span
              className="inline-block text-primary-400 font-medium text-lg mb-4 px-4 py-2 bg-primary-500/10 rounded-full border border-primary-500/20"
              variants={itemVariants}
            >
              Career Journey
            </motion.span>
            <motion.h2
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
              variants={itemVariants}
            >
              Work{" "}
              <span className="text-primary-400">
                Experience
              </span>
            </motion.h2>
            <motion.p
              className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed"
              variants={itemVariants}
            >
              A journey through my professional career, showcasing growth,
              achievements, and the technologies I've mastered along the way.
            </motion.p>
          </motion.div>

          {/* Experience Stats */}
          <motion.div
            variants={itemVariants}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16"
          >
            {statsData.map((stat, index) => (
              <motion.div
                key={stat.label}
                className="text-center p-6 bg-gray-800 rounded-2xl shadow-lg border border-gray-700 hover:border-primary-500 transition-all duration-300"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 + 0.5 }}
                whileHover={{ scale: 1.05, y: -5 }}
              >
                <div className="text-2xl md:text-3xl font-bold text-primary-400 mb-1">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-400">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>

          {/* View Toggle */}
          <motion.div
            variants={itemVariants}
            className="flex justify-center mb-12"
          >
            <div className="flex bg-gray-800 rounded-full p-2 shadow-lg border border-gray-700">
              <button
                onClick={() => handleViewToggle("timeline")}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                  activeView === "timeline"
                    ? "bg-primary-500 text-white shadow-lg shadow-primary-500/25"
                    : "text-gray-400 hover:text-primary-400"
                }`}
              >
                Timeline View
              </button>
              <button
                onClick={() => handleViewToggle("companies")}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                  activeView === "companies"
                    ? "bg-primary-500 text-white shadow-lg shadow-primary-500/25"
                    : "text-gray-400 hover:text-primary-400"
                }`}
              >
                Company Cards
              </button>
            </div>
          </motion.div>

          {/* Content Area */}
          <AnimatePresence mode="wait">
            {isLoading ? (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex justify-center items-center py-20"
              >
                <div className="flex items-center space-x-4">
                  <motion.div
                    className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full"
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  />
                  <span className="text-gray-400">
                    Loading experience data...
                  </span>
                </div>
              </motion.div>
            ) : activeView === "timeline" ? (
              <motion.div
                key="timeline"
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 50 }}
                transition={{ duration: 0.5 }}
              >
                {/* Enhanced Timeline */}
                <div className="relative">
                  {/* Timeline connecting line */}
                  <div className="absolute left-1/2 -translate-x-1/2 w-1 bg-primary-500 h-full hidden md:block rounded-full"></div>

                  {transformedExperience.map((job, index) => (
                    <motion.div
                      key={job.id}
                      className={`flex flex-col md:flex-row items-center mb-16 ${
                        index % 2 !== 0 ? "md:flex-row-reverse" : ""
                      }`}
                      initial={{ opacity: 0, y: 50 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.2 }}
                      whileHover={{ scale: 1.02 }}
                    >
                      {/* Enhanced Job Card */}
                      <motion.div
                        className="w-full md:w-1/2 p-6 md:p-8 rounded-2xl shadow-xl bg-gray-800 border border-gray-700 hover:border-primary-500 hover:shadow-2xl hover:shadow-primary-500/20 transition-all duration-500 backdrop-blur-sm"
                        whileHover={{ y: -5 }}
                      >
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="text-2xl font-bold text-white mb-2">
                              {job.position}
                            </h3>
                            <h4 className="text-lg text-primary-400 font-semibold mb-2">
                              {job.company}
                            </h4>
                          </div>
                          <span className="text-sm text-gray-400 font-medium bg-gray-700 px-3 py-1 rounded-full">
                            {job.duration}
                          </span>
                        </div>

                        {job.location && (
                          <div className="flex items-center text-sm text-gray-400 mb-4">
                            <svg
                              className="w-4 h-4 mr-2"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                                clipRule="evenodd"
                              />
                            </svg>
                            {job.location}
                          </div>
                        )}

                        <p className="text-gray-300 leading-relaxed mb-6">
                          {job.description}
                        </p>

                        <div className="mb-6">
                          <h5 className="text-sm font-semibold text-gray-300 mb-3">
                            Key Responsibilities:
                          </h5>
                          <ul className="space-y-2">
                            {job.responsibilities.map((resp, respIndex) => (
                              <motion.li
                                key={respIndex}
                                className="text-sm text-gray-400 flex items-start"
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{
                                  delay: index * 0.2 + 0.1 * respIndex,
                                }}
                              >
                                <span className="w-2 h-2 bg-primary-500 rounded-full mt-1.5 mr-3 flex-shrink-0"></span>
                                {resp}
                              </motion.li>
                            ))}
                          </ul>
                        </div>

                        {/* Technologies */}
                        {job.technologies && job.technologies.length > 0 && (
                          <div className="pt-4 border-t border-gray-700">
                            <div className="flex flex-wrap gap-2">
                              {job.technologies.map((tech, idx) => (
                                <span
                                  key={tech}
                                  className="px-3 py-1 bg-gray-700 text-gray-300 text-xs rounded-full border border-gray-600"
                                >
                                  {tech}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </motion.div>

                      {/* Enhanced Timeline Dot */}
                      <motion.div
                        className="hidden md:flex w-12 h-12 rounded-full bg-primary-500 z-10 mx-8 border-4 border-gray-900 shadow-lg items-center justify-center"
                        whileHover={{ scale: 1.2 }}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{
                          delay: index * 0.2 + 0.3,
                          type: "spring",
                        }}
                      >
                        <div className="w-4 h-4 bg-white rounded-full"></div>
                      </motion.div>

                      {/* Spacer on mobile */}
                      <div className="block md:hidden h-4"></div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="companies"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.5 }}
                className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
              >
                {companyData.map((company, index) => (
                  <CompanyCard
                    key={company.id}
                    company={company}
                    index={index}
                    isActive={index === activeCompany}
                    onClick={() => setActiveCompany(index)}
                    isVisible={isIntersecting}
                  />
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Certifications */}
          <motion.div variants={itemVariants} className="mt-16">
            <div className="text-center mb-8">
              <span className="inline-block text-primary-400 font-medium text-lg px-4 py-2 bg-primary-500/10 rounded-full border border-primary-500/20">
                Certifications
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {certifications.map((cert, index) => (
                <motion.div
                  key={cert.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.08 }}
                  whileHover={{ y: -4 }}
                  className="group"
                >
                  {cert.verificationUrl ? (
                    <a
                      href={cert.verificationUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-4 p-5 bg-gray-800 border border-gray-700 hover:border-primary-500 hover:shadow-xl hover:shadow-primary-500/10 rounded-2xl transition-all duration-300 h-full"
                    >
                      <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-primary-500 flex items-center justify-center shadow-lg text-white text-lg">
                        🏆
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-semibold text-sm leading-tight">{cert.name}</p>
                        <p className="text-primary-400 text-xs mt-0.5 font-medium">{cert.issuer}</p>
                        {cert.date && <p className="text-gray-500 text-xs mt-1">{cert.date}</p>}
                      </div>
                      <div className="flex-shrink-0 text-gray-500 group-hover:text-primary-400 transition-colors">
                        <svg viewBox="0 0 24 24" className="w-4 h-4 fill-none stroke-current stroke-2">
                          <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    </a>
                  ) : (
                    <div className="flex items-center gap-4 p-5 bg-gray-800 border border-gray-700 rounded-2xl h-full">
                      <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-primary-500 flex items-center justify-center shadow-lg text-white text-lg">
                        🏆
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-semibold text-sm leading-tight">{cert.name}</p>
                        <p className="text-primary-400 text-xs mt-0.5 font-medium">{cert.issuer}</p>
                        {cert.date && <p className="text-gray-500 text-xs mt-1">{cert.date}</p>}
                      </div>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Call to Action */}
          <motion.div variants={itemVariants} className="text-center mt-16">
            <motion.div
              className="inline-block p-8 bg-primary-600 rounded-3xl text-white shadow-2xl shadow-primary-500/25"
              whileHover={{ scale: 1.05, y: -5 }}
              transition={{ duration: 0.3 }}
            >
              <h3 className="text-2xl font-bold mb-4">
                Ready to Work Together?
              </h3>
              <p className="text-primary-100 mb-6 max-w-md">
                Let's discuss how my experience can contribute to your next
                project.
              </p>
              <motion.button
                className="bg-white text-primary-600 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Get In Touch
              </motion.button>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default ExperienceSection;
